import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Spinner } from 'reactstrap';
import { MenuItem } from '@mui/material';
import {
    CheckboxInput,
    FlexDiv,
    RadioTabInput,
    TextDefault,
    TextInput,
    TextLead,
    ThemedButton,
} from '@awarego-ui/components';
import { useStores } from '../../../hooks/use-stores';
import ErrorMessage from '../../../components/errormessage';

const emptyForm = {
    transport: 'internal',
    host: '',
    port: '',
    username: '',
    password: '',
    secure: false,
    tlsRejectUnauthorized: true,
    fromEmail: '',
    fromName: '',
    entityName: 'partner',
};

function SmtpSettingsTab({ companyId }) {
    const { companyStore, store, commonStore } = useStores();
    const { smtpSettings, loadingSmtpSettings, savingSmtpSettings, error } =
        companyStore;
    const { currentCompany } = store;

    const [form, setForm] = useState(emptyForm);
    const [changingPassword, setChangingPassword] = useState(false);

    useEffect(() => {
        if (companyId) {
            companyStore.loadSmtpSettings(companyId);
        }
    }, [companyId, companyStore]);

    useEffect(() => {
        if (!smtpSettings) return;
        setForm({
            transport: smtpSettings.transport || 'internal',
            host: smtpSettings.host || '',
            port: smtpSettings.port || '',
            username: smtpSettings.username || '',
            password: '',
            secure: !!smtpSettings.secure,
            tlsRejectUnauthorized:
                smtpSettings.tlsRejectUnauthorized !== false,
            fromEmail: smtpSettings.fromEmail || '',
            fromName: smtpSettings.fromName || '',
            entityName: smtpSettings.entityName || 'partner',
        });
        setChangingPassword(false);
    }, [smtpSettings]);

    const isSmtp = form.transport === 'smtp';
    const isPasswordSet = !!smtpSettings?.isPasswordSet;
    const showPasswordField = !isPasswordSet || changingPassword;
    const partnerName = smtpSettings?.partnerName || '';
    const companyName =
        smtpSettings?.companyName || currentCompany?.name || '';

    const isDirty = useMemo(() => {
        if (!smtpSettings) return false;
        if ((smtpSettings.transport || 'internal') !== form.transport)
            return true;
        if ((smtpSettings.host || '') !== form.host) return true;
        if ((smtpSettings.port || '') !== form.port) return true;
        if ((smtpSettings.username || '') !== form.username) return true;
        if (!!smtpSettings.secure !== !!form.secure) return true;
        if (
            (smtpSettings.tlsRejectUnauthorized !== false) !==
            !!form.tlsRejectUnauthorized
        )
            return true;
        if ((smtpSettings.fromEmail || '') !== form.fromEmail) return true;
        if ((smtpSettings.entityName || 'partner') !== form.entityName)
            return true;
        if (form.entityName === 'custom') {
            if ((smtpSettings.fromName || '') !== form.fromName) return true;
        }
        if (form.password) return true;
        return false;
    }, [smtpSettings, form]);

    const setField = (name, value) => {
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        const payload = {
            transport: form.transport,
            host: form.host,
            port: form.port,
            username: form.username,
            secure: form.secure,
            tlsRejectUnauthorized: form.tlsRejectUnauthorized,
            fromEmail: form.fromEmail,
            entityName: form.entityName,
            fromName: form.entityName === 'custom' ? form.fromName : '',
        };
        if (form.password) {
            payload.password = form.password;
        }
        try {
            await companyStore.saveSmtpSettings(companyId, payload);
            commonStore.success('SMTP settings saved');
        } catch (e) {
            // error is shown via companyStore.error
        }
    };

    if (loadingSmtpSettings && !smtpSettings) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                <Spinner />
            </div>
        );
    }

    return (
        <div className="row">
            <div className="col-md-8">
                <br />
                <ErrorMessage error={error} />
                <FlexDiv mb={16} alignCenter gap={16}>
                    <TextLead bold>Delivery</TextLead>
                    <RadioTabInput
                        boolean={form.transport === 'internal'}
                        buttonText1={<TextLead bold>Internal</TextLead>}
                        buttonText2={<TextLead bold>SMTP</TextLead>}
                        onChange={(internal) =>
                            setField(
                                'transport',
                                internal ? 'internal' : 'smtp'
                            )
                        }
                    />
                </FlexDiv>
                <TextDefault lighter mb={24}>
                    Internal uses AwareGO mail (Mailgun). SMTP sends through
                    your server from a dedicated IP.
                </TextDefault>

                <div className="form-group">
                    <TextInput
                        label="Entity name"
                        select
                        value={form.entityName}
                        onChange={(e) => setField('entityName', e.target.value)}
                        stretched
                    >
                        <MenuItem value="partner">
                            Partner name
                            {partnerName ? ` [${partnerName}]` : ''}
                        </MenuItem>
                        <MenuItem value="company">
                            Company name
                            {companyName ? ` [${companyName}]` : ''}
                        </MenuItem>
                        <MenuItem value="custom">Custom</MenuItem>
                    </TextInput>
                    <TextDefault lighter mt={8}>
                        Name shown in the From header and in the email body.
                    </TextDefault>
                </div>
                {form.entityName === 'custom' && (
                    <div className="form-group">
                        <TextInput
                            label="Custom name"
                            value={form.fromName}
                            onChange={(e) =>
                                setField('fromName', e.target.value)
                            }
                            stretched
                        />
                    </div>
                )}
                <div className="form-group">
                    <TextInput
                        label="From email"
                        placeholder="security@company.com"
                        value={form.fromEmail}
                        onChange={(e) => setField('fromEmail', e.target.value)}
                        stretched
                    />
                </div>

                {isSmtp && (
                    <>
                        <div className="form-group">
                            <TextInput
                                label="SMTP host"
                                placeholder="smtp.example.com"
                                value={form.host}
                                onChange={(e) =>
                                    setField('host', e.target.value)
                                }
                                stretched
                            />
                        </div>
                        <div className="form-group">
                            <TextInput
                                label="Port"
                                placeholder="587"
                                value={form.port}
                                onChange={(e) =>
                                    setField('port', e.target.value)
                                }
                                stretched
                            />
                        </div>
                        <div className="form-group">
                            <TextInput
                                label="Username"
                                value={form.username}
                                onChange={(e) =>
                                    setField('username', e.target.value)
                                }
                                stretched
                            />
                        </div>
                        <div className="form-group">
                            {showPasswordField ? (
                                <>
                                    <TextInput
                                        label="Password"
                                        type="password"
                                        autoComplete="new-password"
                                        value={form.password}
                                        onChange={(e) =>
                                            setField('password', e.target.value)
                                        }
                                        stretched
                                    />
                                    {isPasswordSet && (
                                        <TextLead
                                            themed
                                            bold
                                            link
                                            role="button"
                                            tabIndex={0}
                                            mt={8}
                                            onClick={() => {
                                                setChangingPassword(false);
                                                setField('password', '');
                                            }}
                                        >
                                            Cancel
                                        </TextLead>
                                    )}
                                </>
                            ) : (
                                <FlexDiv alignCenter gap={16}>
                                    <TextLead bold lighter>
                                        Password
                                    </TextLead>
                                    <TextLead
                                        themed
                                        bold
                                        link
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => setChangingPassword(true)}
                                    >
                                        Change
                                    </TextLead>
                                </FlexDiv>
                            )}
                        </div>
                        <div className="form-group">
                            <CheckboxInput
                                label="Implicit TLS (SMTPS, typically port 465)"
                                checked={form.secure}
                                onChange={(e) =>
                                    setField('secure', e.target.checked)
                                }
                            />
                        </div>
                        <div className="form-group">
                            <CheckboxInput
                                label="Verify SMTP server TLS certificate"
                                checked={form.tlsRejectUnauthorized}
                                onChange={(e) =>
                                    setField(
                                        'tlsRejectUnauthorized',
                                        e.target.checked
                                    )
                                }
                            />
                        </div>
                    </>
                )}

                <div className="form-group">
                    {savingSmtpSettings ? (
                        <Spinner size="sm" />
                    ) : (
                        <ThemedButton
                            mode="secondary"
                            onClick={handleSave}
                            airy
                            text="Save"
                            disabled={
                                !isDirty ||
                                (isSmtp && !form.host.trim()) ||
                                (form.entityName === 'custom' &&
                                    !form.fromName.trim())
                            }
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

export default observer(SmtpSettingsTab);
